// Frontend thin wrapper — delegates the actual header-set to the SDK's
// `attachImpersonationHeader`, but owns the decision of WHICH requests receive
// the `X-Morfi-Impersonate-Grant` grant token.
//
// The grant must ride every Morfi backend request while impersonation is
// active, in BOTH runtime modes:
//   - dev:  `SdkV2Provider` sets the SDK `apiUrl` to '' so the SDK (and the
//           impersonation API client) issue RELATIVE `/v1/*` URLs, which Next
//           `rewrites()` proxy SAME-ORIGIN to the per-network backend. The old
//           implementation compared the request URL against the ABSOLUTE
//           network API origin, so a relative `/v1/...` never matched and the
//           header silently never attached — impersonated reads returned the
//           engineer's own data.
//   - prod: the SDK targets the absolute per-network API origin directly.
//
// It must NEVER attach to third-party requests (Helius RPC, Statsig,
// …) — that would leak the grant token (FR-044) — nor to another network's API
// (cross-network replay defence, FR-045).

import { attachImpersonationHeader as sdkAttachImpersonationHeader } from '@morfi/backend-sdk-v2/client';

import { getNetworkConfig } from '@/lib/network';
import { useImpersonationStore } from './store';

export { IMPERSONATION_HEADER_NAME } from '@morfi/backend-sdk-v2/client';

export function attachImpersonationHeader(url: string, init: RequestInit | undefined): RequestInit {
  const state = useImpersonationStore.getState();
  if (!state.isActive || state.grantToken === null || state.network === null) {
    return init ?? {};
  }
  if (!requestTargetsMorfiApi(url, state.network)) {
    return init ?? {};
  }
  // Confirmed: active grant + a request to the grant's own Morfi API. Pass an
  // empty `expectedApiOrigin` so the SDK helper skips its own string-prefix
  // origin check (which cannot match dev's relative URLs) and just sets the
  // header — the routing decision was already made above.
  const withHeader = sdkAttachImpersonationHeader(url, init, state, '');
  return attachImpersonationAbortSignal(withHeader, state.abortController);
}

function attachImpersonationAbortSignal(
  init: RequestInit,
  abortController: AbortController | null,
): RequestInit {
  if (abortController === null) return init;
  const requestSignal = init.signal;
  if (requestSignal === abortController.signal) return init;
  if (requestSignal === null || requestSignal === undefined) {
    return { ...init, signal: abortController.signal };
  }
  return { ...init, signal: AbortSignal.any([requestSignal, abortController.signal]) };
}

/**
 * True iff `url` targets the Morfi backend for `network` — the absolute
 * per-network API origin (prod) OR a same-origin `/v1/*` path that Next proxies
 * to that backend (dev). Never true for third-party or other-network URLs.
 */
function requestTargetsMorfiApi(url: string, network: string): boolean {
  const pageOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;

  let resolved: URL;
  try {
    resolved = new URL(url, pageOrigin ?? 'http://localhost');
  } catch {
    // SILENT-CATCH: an unparseable URL is not a Morfi API request — never attach the grant.
    return false;
  }

  // Prod / absolute-API mode: compare by ORIGIN (scheme+host) so the `/api/v1`
  // vs `/v1` path difference between the config URL and the SDK's stripped base
  // doesn't matter.
  const apiUrl = getNetworkConfig(network as 'mainnet' | 'devnet' | 'local').apiUrl;
  if (apiUrl) {
    try {
      if (resolved.origin === new URL(apiUrl).origin) return true;
    } catch {
      // SILENT-CATCH: malformed config URL — fall through to the same-origin check.
    }
  }

  // Dev / same-origin proxy mode: a relative `/v1/*` (resolved against the page
  // origin) is rewritten to the active network's backend. Impersonation is torn
  // down on any network toggle, so an active grant here always matches the
  // proxied network — no cross-network leak.
  if (pageOrigin !== undefined && resolved.origin === pageOrigin) {
    return resolved.pathname === '/v1' || resolved.pathname.startsWith('/v1/');
  }

  return false;
}
